(ns frontend.view.user-management.components
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [basic-components.inputs.footprint :refer [editor]]
   [basic-components.tabstrip-v2 :as tabstrip]
   [clojure.data :as data]
   [clojure.set :as set]
   [common.localization :as loc]
   [common.roles :as roles]
   [common.security.authentication :as auth]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.account-disable.view :as account-disable]
   [frontend.view.modals.account-reactivate.view :as account-reactivate]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.entity-editor.view :as entity-editor-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.team-edit.view :as team-edit]
   [frontend.view.profile.events :as profile-events]
   [frontend.view.user-management.events :as events]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.toggle.view :as toggle]))

(defn clear-selected!
  [local-state]
  (let [clear-selection (fn []
                          (if-let [grid-ref (get-in @local-state [:grid :ref])]
                            (oops/ocall! grid-ref "api.deselectAll" nil "apiDeselectAll")
                            (js/console.log "Could not resolve grid api " @local-state)))]

    (clear-selection)))

(defn clear-external-state!
  [external-state]
  (swap! external-state dissoc
         :selected-group-id
         :selected-team-id
         :selected-account-id))

(defn tabstrip
  [state]
  [tabstrip/component
   {:on-change!       (fn [new-tab-key]
                        (swap! state assoc :selected-tab new-tab-key)
                        (swap! state dissoc :show-deactivated? :show-deactivated-guests?)
                        (clear-external-state! state)
                        (routing/set-view! [new-tab-key]))
    :selected-tab-key (:selected-tab @state)
    :tab-strip-id     "user-management-tabs"
    :tabs             (concat [{:tab-key   :settings/users
                                :tab-label (loc/tr :settings/users)}]
                              (when (or (session/dealflow?)
                                        (session/portfolio?)
                                        (session/kpi-reporting?)
                                        (session/has-feature-access? :feature/sell-side-guest)
                                        (session/has-feature-access? :feature/buy-side-guest))
                                [{:tab-key   :settings/guest-accounts
                                  :tab-label (loc/tr :settings/guest-accounts)}])
                              [{:tab-key   :settings/teams
                                :tab-label (loc/tr :settings/teams)}]
                              (when (session/has-feature-access? :feature/insider-groups)
                                [{:tab-key   :settings/insider-groups
                                  :tab-label (loc/tr :settings/insider-groups)}]))}])

(defn reset-2fa-button
  [selected-account-id]
  [button/secondary-open
   {:disabled? (not selected-account-id)
    :class     "!text-sm"
    :on-click  (without-default-action-and-propagation
                (modals/open-modal!
                 confirmation/modal-key
                 {:body          [:div
                                  [:p
                                   (loc/tr (if (= selected-account-id @session/account-id)
                                         :mfa-setup-confirm
                                         :mfa-setup-confirm-other))]]
                  :confirm-text  (loc/tr :mfa-setup-confirm-button)
                  :checkbox-text (loc/tr :i-understand-theres-no-undo-for-this)
                  :confirm-icon  nil
                  :on-confirm    #(profile-events/deactivate-mfa! selected-account-id)}))}
   [:i.fal.fa-shield-check]
   (loc/tr :mfa-reset)])

(defn show-insider-history
  [group-id]
  [button/helper-button
   {:on-click  (fn [_])
    :disabled? (not group-id)}
   [:i.fal.fa-clock]
   (loc/tr :show-history)])

(defn team-tools
  [{:keys [external-state local-state]}]
  (let [all-teams @session/teams
        team-id (:selected-team-id @external-state)
        delete-disabled (-> team-id
                            (session/get-entity)
                            :team/members
                            (seq))]
    [:div
     {:class-name "flex items-center gap-3"}
     [button/helper-button
      {:on-click  (fn [_]
                    (modals/open-modal! team-edit/modal-key
                                        {:team-id        team-id
                                         :existing-teams all-teams
                                         :on-close!      (fn []
                                                           (clear-selected! local-state)
                                                           (clear-external-state! external-state))}))
       :disabled? (not team-id)}
      [:i.fal.fa-pen]
      (loc/tr :edit-team)]

     [button/helper-button
      {:tooltip   (loc/tr :cannot-delete-team-with-members)
       :on-click  (fn []
                    (when-not delete-disabled
                      (modals/open-modal!
                       confirmation/modal-key
                       {:body          (loc/tr :team-delete)
                        :confirm-text  (loc/tr :confirm-delete-selected-button)
                        :checkbox-text (loc/tr :i-understand-theres-no-undo-for-this)
                        :confirm-icon  nil
                        :on-confirm    (fn []
                                         (events/disable-team! team-id))
                        :on-cancel     (fn []
                                         (clear-external-state! external-state)
                                         (clear-selected! local-state))})))
       :disabled? (or (not team-id)
                      delete-disabled)}
      [:i.fal.fa-trash]
      (loc/tr :delete-team)]]))

(defn edit-insider-group
  [{:keys [group-id external-state local-state]}]
  [button/helper-button
   {:disabled? (not group-id)
    :on-click  (fn [_]
                 (let [state (r/atom (session/get-entity group-id))]
                   (modals/open-modal!
                    entity-editor-modal/modal-key
                    {:entity-id               group-id
                     :entity-state            state
                     :on-ok!                  (fn [entity-id close!]
                                                (let [group-keys [:insider-group/name
                                                                  :insider-group/members
                                                                  :insider-group/teams
                                                                  :insider-group/status]
                                                      current-vals (select-keys (get @session/entities entity-id)
                                                                                group-keys)
                                                      new-vals (select-keys @state group-keys)
                                                      [removed added common] (data/diff current-vals new-vals)
                                                      updates (merge added
                                                                     (when (or (:insider-group/members added)
                                                                               (:insider-group/members removed))

                                                                       {:insider-group/members (set/union (set (:insider-group/members common))
                                                                                                          (set (:insider-group/members added)))})
                                                                     (when (or (:insider-group/teams added)
                                                                               (:insider-group/teams removed))
                                                                       {:insider-group/teams (set/union (set (:insider-group/teams common))
                                                                                                        (set (:insider-group/teams added)))}))
                                                      no-updates (empty? updates)
                                                      close-and-clear! (fn []
                                                                         (close!)
                                                                         (clear-external-state! external-state)
                                                                         (clear-selected! local-state))]
                                                  (if no-updates
                                                    (close-and-clear!)
                                                    (entity-actions/*->entity-update!
                                                     entity-id
                                                     updates
                                                     close-and-clear!
                                                     (fn [err]
                                                       (logging/error "Failed to update insider group" err))))))
                     :on-cancel!              (fn []
                                                (clear-external-state! external-state)
                                                (clear-selected! local-state))
                     :no-changes?             (fn []
                                                (let [edit-keys [:insider-group/name
                                                                 :insider-group/members
                                                                 :insider-group/teams
                                                                 :insider-group/status]
                                                      current-vals (select-keys (get @session/entities group-id)
                                                                                edit-keys)
                                                      new-vals (select-keys @state edit-keys)
                                                      [removed added _common] (data/diff current-vals new-vals)]
                                                  (not (or removed added))))
                     :skip-success-msg?       true
                     :prevent-backend-update? true
                     :title-text              (loc/tr :edit-insider-group)
                     :mandatory-fields        [:insider-group/name
                                               :insider-group/status]})))}
   [:i.fal.fa-pen]
   (loc/tr :edit-insider-group)])

(defn delete-insider-group
  [{:keys [group-id delete-disabled external-state local-state]}]
  [button/helper-button
   {:tooltip   (loc/tr :cannot-delete-team-with-members)
    :on-click  (fn []
                 (when-not delete-disabled
                   (modals/open-modal!
                    confirmation/modal-key
                    {:body          (loc/tr :insider-group-delete)
                     :confirm-text  (loc/tr :confirm-delete-selected-button)
                     :checkbox-text (loc/tr :i-understand-theres-no-undo-for-this)
                     :confirm-icon  nil
                     :on-confirm    (fn []
                                      (events/disable-insider-group! group-id))
                     :on-cancel     (fn []
                                      (clear-external-state! external-state)
                                      (clear-selected! local-state))})))
    :disabled? (or (not group-id)
                   delete-disabled)}
   [:i.fal.fa-trash]
   (loc/tr :delete-insider-group)])

(defn insider-tools
  [external-state local-state]
  (let [group-id (:selected-group-id @external-state)
        delete-disabled (seq (concat (->> group-id
                                          (session/get-entity)
                                          :insider-group/members
                                          (map session/get-entity)
                                          (remove :common/disabled?))
                                     (->> group-id
                                          (session/get-entity)
                                          :insider-group/teams
                                          (map session/get-entity)
                                          (remove :common/disabled?))))]

    [:<>
     [edit-insider-group
      {:group-id       group-id
       :external-state external-state
       :local-state    local-state}]
     [show-insider-history group-id]
     [delete-insider-group
      {:group-id        group-id
       :delete-disabled delete-disabled
       :external-state  external-state
       :local-state     local-state}]]))

(defn user-tools
  [{:keys [external-state local-state]}]
  (let [selected-account-id (:selected-account-id @external-state)
        selected-account (session/get-entity selected-account-id)
        tenant-id (:common/tenant selected-account)
        account-role (->> @session/all-roles
                          (remove (fn [r]
                                    (roles/is-guest-role? (:role/role r))))
                          (cu/filter-by :role/target tenant-id)
                          (cu/filter-by :role/subject selected-account-id)
                          (first))
        role-kw (:role/role account-role)
        tenant @session/tenant
        account-role-id (:db/id account-role)
        disabled-role? (:common/disabled? account-role)]
    [:div
     {:class-name "flex items-center gap-3"}
     [button/helper-button
      {:disabled? (or disabled-role?
                      (not selected-account-id))
       :on-click  (fn [_]
                    (modals/open-modal! account-reactivate/modal-key
                                        {:account-id         selected-account-id
                                         :role-id            account-role-id
                                         :allow-email-edit?  true
                                         :title              (loc/tr :edit-user-details)
                                         :submit-button-text (loc/tr :save)
                                         :success-message    (loc/tr :success-account-modified)
                                         :on-close!          (fn []
                                                               (clear-external-state! external-state)
                                                               (clear-selected! local-state))}))}
      [:i.fal.fa-pen]
      (loc/tr :settings/edit-account)]

     (when (and (not disabled-role?)
                (not (roles/is-guest-role? role-kw))
                (= (:account/mfa-status selected-account) :mfa.status/assigned)
                (auth/mfa-on? tenant))
       [reset-2fa-button selected-account-id])

     [button/helper-button
      {:disabled? (or (:common/disabled? account-role)
                      (not selected-account-id))
       :on-click  #(events/re-invite! {:account    selected-account
                                       :role       account-role
                                       :on-submit! identity})}
      [:i.fal.fa-paper-plane]
      (loc/tr :settings/re-send-invitation)]

     [button/helper-button
      {:disabled? (or disabled-role?
                      (= selected-account-id @session/account-id)
                      (not selected-account-id))
       :on-click  #(modals/open-modal!
                    account-disable/modal-key
                    {:account    selected-account
                     :role       account-role
                     :on-submit! (fn []
                                   (clear-external-state! external-state)
                                   (clear-selected! local-state))
                     :on-close!  (fn []
                                   (clear-external-state! external-state)
                                   (clear-selected! local-state))})}
      [:i.fal.fa-user-times]
      (loc/tr :settings/deactivate)]

     [button/helper-button
      {:disabled? (or (not disabled-role?)
                      (not selected-account-id))
       :on-click  #(modals/open-modal! account-reactivate/modal-key
                                       {:account-id selected-account-id
                                        :role-id    account-role-id
                                        :on-submit! (fn []
                                                      (clear-external-state! external-state)
                                                      (clear-selected! local-state))
                                        :on-close!  (fn []
                                                      (clear-external-state! external-state)
                                                      (clear-selected! local-state))})}
      [:i.fal.fa-user-plus]
      (loc/tr :settings/reactivate)]]))

(defn search-bar
  [value update-search-term! state]
  [editor
   {:editor/type         :editor.type/text-simple
    :default-value       (loc/tr :search-default-value)
    :placeholder         (loc/tr :search-default-value)
    :class-name          "py-2 px-3 w-full text-input-light-text placeholder:text-input-light-text-paler outline-0 border border-solid border-input-light-border-paler hover:border-input-light-border focus:ring-1 focus:ring-inset focus:ring-input-light-border focus-visible:ring-input-light-border rounded-full bg-base-light"
    :clearable           false
    :value               value
    :on-change!          (fn [_e new-value]
                           (clear-external-state! state)
                           (update-search-term! new-value))
    :disabled?           false
    :read-only?          false
    :secondary-view-args nil}])

(defn not-found-view
  []
  [:div
   {:class "py-5 flex flex-col justify-center items-center gap-3"}
   [:div
    [:i
     {:class "fas fa-user-circle text-4xl text-base-light-text-paler"}]]

   [:div
    {:class "text-center font-bold"}
    (loc/tr :no-results-found)]])

(defn not-guests-invited
  [{:keys [has-buy-side-guests? has-sell-side-guests? _has-lp-guests? lp-reporting-users?]}]
  [:div
   {:class "py-5 flex flex-col justify-center items-center gap-3"}
   [:i
    {:class "fas fa-user-circle text-4xl text-base-light-text-paler"}]
   [:div
    {:class "font-bold text-xl"}
    (loc/tr :no-guests-invited-yet)]

   (when-not lp-reporting-users?
     [:<>
      [:div
       {:class "text-center"}
       (loc/tr :no-guests-invited-text)
       [:p (loc/tr :no-guests-invited-text-2)]]
      [:div
       {:class "pt-4"}
       (cond
         (and (session/has-feature-access? :feature/sell-side-guest)
              (not has-sell-side-guests?))
         [:a
          {:href  "https://help.zapflow.com/en/articles/2143792-inviting-a-sell-side-guest-user"
           :class "text-base-light-text-accent font-bold hover:underline underline-offset-4"}
          (loc/tr :inviting-sell-side-quest)]
         (and (session/has-feature-access? :feature/sell-side-guest)
              (session/has-feature-access? :feature/buy-side-guest)
              (not has-buy-side-guests?)
              (not has-sell-side-guests?))
         [:span
          (loc/tr :or)]
         (and (session/has-feature-access? :feature/buy-side-guest)
              (not has-buy-side-guests?))
         [:a
          {:href  "https://help.zapflow.com/en/articles/3011095-inviting-a-buy-side-guest-user"
           :class "text-base-light-text-accent font-bold hover:underline underline-offset-4"}
          (loc/tr :inviting-buy-side-quest)])]])])

(defn table-toggle-title
  [{:keys [expanded? toggle-expanded! title]}]
  [:span
   {:on-click toggle-expanded!
    :class    "py-2 flex items-center gap-2 font-bold hover:text-base-light-text-accent cursor-pointer"}
   title
   (if expanded?
     [:i.fal.fa-chevron-down]
     [:i.fal.fa-chevron-right])])

(defn toggle-cell-renderer
  [on-change!
   {:keys [column-meta cell-value entity-id state
           row-data]}]
  [:div
   {:class "flex justify-start"}
   [toggle/toggle
    {:checked?  cell-value
     :disabled? (= "deactivated" (:active? row-data))
     :on-change (fn [toggled?]
                  (on-change! {:state       state
                               :entity-id   entity-id
                               :column-meta column-meta
                               :on?         toggled?}))}]])

(defn toggle-user-team-cell-renderer
  [on-change! team {:keys [column-key cell-value row-data state]}]
  (let [admin-to-read-only-team? (and (:admin? row-data)
                                      (:team/read-only-team? team))
        disabled? (or (= "deactivated" (:active? row-data))
                      admin-to-read-only-team?)]
    (fu/tooltip-overlay-if
     {:placement "bottom"}
     admin-to-read-only-team?
     (loc/tr :admin-cannot-belong-to-read-only-team)
     [:div
      {:class "flex justify-start"}
      [toggle/toggle
       {:checked?  cell-value
        :disabled? disabled?
        :on-change (fn [toggled?]
                     (on-change! {:state      state
                                  :row-data   row-data
                                  :column-key column-key
                                  :on?        toggled?}))}]])))

(defn toggle-admin-cell-renderer
  [on-change! {:keys [column-meta cell-value row-data state]}]
  (let [disabled? (or (= "deactivated" (:active? row-data))
                      (boolean (:belongs-to-read-only-team? row-data)))]
    (fu/tooltip-overlay-if
     {:placement "bottom"}
     disabled?
     (loc/tr :admin-cannot-belong-to-read-only-team)
     [:div
      {:class "flex justify-start"}
      [toggle/toggle
       {:checked?  cell-value
        :disabled? disabled?
        :on-change (fn [toggled?]
                     (on-change! {:state       state
                                  :row-data    row-data
                                  :column-meta column-meta
                                  :on?         toggled?}))}]])))

(defn toggle-team-read-only-renderer
  [on-change! {:keys [column-meta cell-value row-data state]}]
  (let [disabled? (boolean (:has-admins? row-data))]
    (fu/tooltip-overlay-if
     {:placement "bottom"}
     disabled?
     (loc/tr :team-with-admins-cannot-be-read-only)
     [:div
      {:class "flex justify-start"}
      [toggle/toggle
       {:checked?  cell-value
        :disabled? disabled?
        :on-change (fn [toggled?]
                     (on-change! {:state       state
                                  :row-data    row-data
                                  :column-meta column-meta
                                  :on?         toggled?}))}]])))



